import { ReturnValue } from '@enzoferey/ethers-error-parser';
import { BigNumberish } from '@ethersproject/bignumber';
import { createSlice, current } from '@reduxjs/toolkit';

import { getNetworkConfig, Tokens } from '../../../constants';
import { ProtocolName } from '../../../constants/protocolNames';
import { GasOption } from '../../../hooks';
import { QuoteInfo } from '../../../services/types';
import { DefaultTokenOptions, Field, ReferrerOptions, SupportedGasOptions } from '../../../types';
import { calculateRoughFees } from '../../../utils/';

export interface SwapState {
  readonly independentField: Field;
  readonly slippage: number;
  readonly typedValue: BigNumberish;
  readonly [Field.INPUT]: string;
  readonly [Field.OUTPUT]: string;
  readonly referrerOptions: ReferrerOptions;
  readonly defaultInputTokenAddress: DefaultTokenOptions;
  readonly defaultOutputTokenAddress: DefaultTokenOptions;
  readonly defaultTypedValue: BigNumberish;
  // the typed recipient address if swap should go to sender
  readonly recipient: string | null;
  readonly txFeeCalculation: {
    readonly gasPriceInfo: GasOption;
    readonly customGasPrice: {
      label: string;
      maxPriorityFee: string;
      maxFee: string;
      range: string;
      timeLabel: string;
    };
    readonly gasPriceSettingsMode: 'basic' | 'advanced';
  };
  readonly loadingQuoteForAll: {
    [protocolName: string]: {
      loadingStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
      quoteId: number;
    };
  };
  readonly errorQuoteForAll: {
    [protocolName: string]: any;
  };
  readonly estimateGasError: ReturnValue | null;
  readonly buildTxError: any;
  readonly lastQuoteUpdateTimestamp: number;
  readonly lastQuoteCallId: number;
  readonly swapData: { [protocolName: string]: QuoteInfo };
  readonly selectedProtocol: string;
  readonly txFees: { [protocolName: string]: string };
}

export const initialState: SwapState = {
  independentField: Field.INPUT,
  slippage: 1,
  typedValue: '0',
  [Field.INPUT]: '',
  [Field.OUTPUT]: '',
  referrerOptions: {},
  defaultInputTokenAddress: {
    1: '',
  },
  defaultOutputTokenAddress: {
    1: '',
  },
  defaultTypedValue: '',
  recipient: null,
  txFeeCalculation: {
    gasPriceInfo: {
      id: SupportedGasOptions.High,
      label: '',
      range: '-- / -- - 0.00 Gwei',
      timeLabel: '',
      price: '0',
      baseFee: '0',
    },
    customGasPrice: {
      label: '',
      maxFee: '0',
      maxPriorityFee: '0',
      range: '',
      timeLabel: '',
    },
    gasPriceSettingsMode: 'basic',
  },
  loadingQuoteForAll: {
    [ProtocolName.ONE_INCH]: {
      loadingStatus: 'idle',
      quoteId: 0,
    },
  },
  errorQuoteForAll: {},
  estimateGasError: null,
  buildTxError: null,
  lastQuoteUpdateTimestamp: -1,
  lastQuoteCallId: -1,
  swapData: {},
  selectedProtocol: ProtocolName.ONE_INCH,
  txFees: {},
};

const swapSlice = createSlice({
  name: 'swap',
  initialState,
  reducers: {
    updateAllQuotes(state, { payload: { quote, loading, error } }) {
      const protocolName = Object.keys(quote)[0];
      // "quoteId" is generated based on the current time of its call and any previous "old" calls are ignored if a "new" quote call is made.
      if (state.loadingQuoteForAll[protocolName]?.quoteId > loading.quoteId) {
        return;
      }
      const fee = calculateRoughFees(quote, state.txFeeCalculation.gasPriceInfo.price);
      return {
        ...state,
        errorQuoteForAll: { ...state.errorQuoteForAll, ...{ [protocolName]: error } },
        loadingQuoteForAll: {
          ...state.loadingQuoteForAll,
          ...{ [protocolName]: loading },
        },
        swapData: { ...state.swapData, ...quote },
        lastQuoteUpdateTimestamp: performance.now(),
        txFees: { ...state.txFees, ...fee },
      };
    },
    setSlippage(state, { payload: { percent } }) {
      state.slippage = percent;
    },
    switchCurrencies(state) {
      return {
        ...state,
        independentField: state.independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT,
        [Field.INPUT]: state[Field.OUTPUT],
        [Field.OUTPUT]: state[Field.INPUT],
      };
    },
    selectCurrency(state, { payload: { currency, field } }) {
      const otherField = field === Field.INPUT ? Field.OUTPUT : Field.INPUT;
      if (currency === state[otherField]) {
        const flipCurrency = state[field as Field];
        // the case where we have to swap the order
        return {
          ...state,
          independentField: state.independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT,
          [field]: currency,
          [otherField]: flipCurrency,
        };
      }

      // the normal case
      return {
        ...state,
        [field]: currency,
      };
    },
    typeInput(state, { payload: { field, typedValue } }) {
      return {
        ...state,
        independentField: field,
        typedValue: typedValue.toString(),
      };
    },
    // Working with default settings
    setDefaultSettings(
      state,
      { payload: { referrerOptions, defaultInputTokenAddress, defaultOutputTokenAddress, defaultTypedValue } }
    ) {
      state.referrerOptions = referrerOptions ?? state.referrerOptions;
      state.defaultInputTokenAddress = defaultInputTokenAddress ?? state.defaultInputTokenAddress;
      state.defaultOutputTokenAddress = defaultOutputTokenAddress ?? state.defaultOutputTokenAddress;
      state.defaultTypedValue = defaultTypedValue ?? state.defaultTypedValue;
    },
    applyDefaultSettings(state, { payload: { chainId } }) {
      const info = getNetworkConfig(chainId);
      const onSupportedChain = info !== undefined;

      // set default tokens ans typedValue if only connector on supported chain id
      if (onSupportedChain) {
        state[Field.INPUT] = state.defaultInputTokenAddress[chainId] || Tokens.FAVORITE_TOKENS[chainId][0];
        // @ts-ignore
        state[Field.OUTPUT] =
          state.defaultOutputTokenAddress[chainId] ||
          Tokens.FAVORITE_TOKENS[chainId].filter((token: string) => token != state[Field.INPUT])[0];
        state.typedValue = state.defaultTypedValue[chainId]?.toString() || '';
      }
    },
    setGasPriceInfo(state, { payload: gasPriceInfo }) {
      return {
        ...state,
        txFeeCalculation: { ...state.txFeeCalculation, gasPriceInfo },
        txFees: calculateRoughFees(current(state.swapData), gasPriceInfo.price),
      };
    },
    setCustomGasPrice(state, { payload: customGasPrice }) {
      return {
        ...state,
        txFeeCalculation: { ...state.txFeeCalculation, customGasPrice },
      };
      // TODO question: should recalculate fees too?
    },
    setGasPriceSettingsMode(state, { payload: mode }) {
      return {
        ...state,
        txFeeCalculation: { ...state.txFeeCalculation, gasPriceSettingsMode: mode },
      };
    },
    selectSwapProtocol(state, { payload: selectedProtocol }) {
      return {
        ...state,
        selectedProtocol,
      };
    },
    setEstimateGasError(state, { payload: estimateGasError }) {
      return {
        ...state,
        estimateGasError,
      };
    },
    setBuildTxError(state, { payload: buildTxError }) {
      return {
        ...state,
        buildTxError,
      };
    },
  },
});

export const {
  setDefaultSettings,
  applyDefaultSettings,
  typeInput,
  selectCurrency,
  switchCurrencies,
  setSlippage,
  setGasPriceInfo,
  setCustomGasPrice,
  setGasPriceSettingsMode,
  selectSwapProtocol,
  updateAllQuotes,
  setEstimateGasError,
  setBuildTxError,
} = swapSlice.actions;

const { reducer } = swapSlice;

export default reducer;
